import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { from } from 'rxjs';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { RemoteDeploymentStoreFacade } from './remote-deployment-db.facade';
import {
  CheckForAppUpdates,
  ClearRemoteDeploymentState,
  NoRemoteDeploymentAction,
  ReFetchAppMasterAndClearState,
  RemoteDeploymentStoreActionTypes,
  SetRemoteDeploymentState,
  UpdateAppMasterAndClearState,
  UpdateRemoteDeploymentState,
} from './remote-deployment-db.actions';
import { ActivationService } from '../../services/activation/activation.service';
import { BuildUtils, InputAppAPIHelperUtil, InputAppErrorDisplay } from '@monorepo/utils';
import { AuthService } from '../../authentication/auth.service';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { TRemoteDeploymentDB } from '@monorepo/types';
import AppUpdater from '../../services/app-updater/app-updater';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class RemoteDeploymentStoreEffects {
  constructor(
    private action$: Actions,
    private persistentStorageService: PersistentStorageService,
    private remoteDeploymentStoreFacade: RemoteDeploymentStoreFacade,
    private activationService: ActivationService,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private authService: AuthService,
    private appMasterDBFacade: AppMasterDBFacade,
    private inputAppErrorUtil: InputAppErrorDisplay,
    private translate: TranslateService,
  ) {}
  CheckForAppUpdates$ = createEffect(() => {
    return this.action$.pipe(
      ofType<CheckForAppUpdates>(RemoteDeploymentStoreActionTypes.CheckForAppUpdates),
      switchMap((action) => {
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'FETCHING_APP_UPDATES',
            this.translate.instant('remote_deployment.fetch_update'),
          ),
        ).pipe(
          take(1),
          map(() => [action] as const),
        );
      }),
      switchMap(([action]) => {
        return this.appMasterDBFacade.getTokenAndSelectedSubject().pipe(
          take(1),
          map((appMaster) => {
            return [appMaster, action] as const;
          }),
        );
      }),
      switchMap(([appMaster, action]) => {
        return from(this.persistentStorageService.get('[APP][INPUT_APP_UPDATES]')).pipe(
          take(1),
          map((existingUpdates) => {
            return [appMaster, action, existingUpdates ? existingUpdates : undefined] as const;
          }),
        );
      }),
      switchMap(([{ subject, hsk, token }, action, existingUpdates]) => {
        const updatedSubject =
          action.remoteDeploymentCheck.appIdentifier === 'tv_dashboard_v2' ? subject + '-tvdashboard' : subject;
        return this.activationService
          .getAppUpdates({
            token,
            subject: updatedSubject,
            hsk,
            contexts: [{ deploymentType: 'APP', appName: 'Tracki8' }],
            versions: [
              {
                context: {
                  appName: 'Tracki8',
                  deploymentType: 'APP',
                },
                version: BuildUtils.APP_VERSION,
              },
            ],
          })
          .pipe(
            take(1),
            switchMap((response) => {
              return from(this.inputAppErrorUtil.dismissLoadingWithId('FETCHING_APP_UPDATES')).pipe(
                take(1),
                switchMap(() => {
                  if (
                    response.status === 'UPDATES_AVAILABLE' &&
                    response.updates &&
                    response.updates[0] &&
                    response.updates[0].featureTypeOpt &&
                    response.updates[0].version &&
                    response.updates[0].updateUrl
                  ) {
                    const appUpdates: TRemoteDeploymentDB = {
                      latestVersionAvailable: response.updates[0].version,
                      featureType: response.updates[0].featureTypeOpt,
                      updateUrl: response.updates[0].updateUrl,
                    };
                    return from(this.persistentStorageService.set('[APP][INPUT_APP_UPDATES]', appUpdates)).pipe(
                      take(1),
                      map(() => {
                        return new UpdateRemoteDeploymentState(appUpdates, action.remoteDeploymentCheck);
                      }),
                    );
                  } else {
                    return from(this.persistentStorageService.remove('[APP][INPUT_APP_UPDATES]')).pipe(
                      take(1),
                      map(() => {
                        if (action.remoteDeploymentCheck.type === 'CHECK_FROM_SETTINGS') {
                          this.inputAppAPIHelperUtil.showPopup(
                            this.translate.instant('remote_deployment.no_update'),
                            '',
                          );
                          return new ClearRemoteDeploymentState();
                        } else {
                          this.remoteDeploymentStoreFacade.initAppData(action.remoteDeploymentCheck);
                          return new ClearRemoteDeploymentState();
                        }
                      }),
                    );
                  }
                }),
              );
            }),
            catchError((error) => {
              return from(this.inputAppErrorUtil.dismissLoadingWithId('FETCHING_APP_UPDATES')).pipe(
                take(1),
                map(() => {
                  if (existingUpdates) {
                    this.remoteDeploymentStoreFacade.showAppUpdatesPopup(action.remoteDeploymentCheck, existingUpdates);
                    return new SetRemoteDeploymentState(existingUpdates);
                  } else {
                    this.remoteDeploymentStoreFacade.showErrorPopupWithResolvedSubHeader(
                      action.remoteDeploymentCheck,
                      this.translate.instant('remote_deployment.err_fetch_update'),
                      true,
                      'Error',
                      false,
                    );
                    return new NoRemoteDeploymentAction();
                  }
                }),
              );
            }),
          );
      }),
    );
  });

  UpdateRemoteDeploymentState$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateRemoteDeploymentState>(RemoteDeploymentStoreActionTypes.UpdateRemoteDeploymentState),
      map((action) => {
        this.remoteDeploymentStoreFacade.showAppUpdatesPopup(action.remoteDeploymentCheck, action.remoteDeploymentDB);
        return new NoRemoteDeploymentAction();
      }),
    );
  });

  ReFetchAppMasterAndClearState$ = createEffect(() => {
    return this.action$.pipe(
      ofType<ReFetchAppMasterAndClearState>(RemoteDeploymentStoreActionTypes.ReFetchAppMasterAndClearState),
      switchMap((action) => {
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'RE_FECTING_APP_MASTER',
            this.translate.instant('remote_deployment.refetch_config'),
          ),
        ).pipe(
          take(1),
          map(() => [action] as const),
        );
      }),
      switchMap(([action]) => {
        return from(this.persistentStorageService.get('[APP_MASTER]')).pipe(
          take(1),
          map((appMaster) => {
            return [appMaster, action] as const;
          }),
        );
      }),
      switchMap(([appMaster, action]) => {
        return this.activationService
          .getMasterAppConfigByTokenAndAppMasterId({
            token: appMaster.activationInfo.token,
            app_master_id: appMaster.appMasterId,
          })
          .pipe(
            take(1),
            switchMap((appMaster) => {
              return from(this.inputAppErrorUtil.dismissLoadingWithId('RE_FECTING_APP_MASTER')).pipe(
                take(1),
                map(() => {
                  return new UpdateAppMasterAndClearState(action.remoteDeploymentCheck, appMaster);
                }),
              );
            }),
            catchError((error) => {
              return from(this.inputAppErrorUtil.dismissLoadingWithId('RE_FECTING_APP_MASTER')).pipe(
                take(1),
                map(() => {
                  this.remoteDeploymentStoreFacade.showErrorPopupWithResolvedSubHeader(
                    action.remoteDeploymentCheck,
                    this.translate.instant('remote_deployment.err_refetch_config'),
                    true,
                    'Error',
                    false,
                  );
                  return new NoRemoteDeploymentAction();
                }),
              );
            }),
          );
      }),
    );
  });

  UpdateAppMasterAndClearState$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateAppMasterAndClearState>(RemoteDeploymentStoreActionTypes.UpdateAppMasterAndClearState),
      switchMap((action) => {
        return from(
          this.inputAppErrorUtil.presentLoadingWithId(
            'UPDATING_APP_MASTER',
            this.translate.instant('remote_deployment.updating_config'),
          ),
        ).pipe(
          take(1),
          map(() => [action] as const),
        );
      }),
      switchMap(([action]) => {
        return from(this.persistentStorageService.get('[APP_MASTER]')).pipe(
          take(1),
          map((appMaster) => {
            return [appMaster, action] as const;
          }),
        );
      }),
      switchMap(([oldAppMaster, action]) => {
        const updatedAppMaster = {
          ...oldAppMaster,
          apps: oldAppMaster.apps.map((appDetails) => {
            const newAppInfo = action.newAppMaster.apps.find((appInfo) => appInfo.app_id === appDetails.appInfo.app_id);
            return {
              ...appDetails,
              appInfo: newAppInfo ? newAppInfo : appDetails.appInfo,
            };
          }),
        };
        console.log('OLD_AND_UPDATED_APP_MASTER', oldAppMaster, updatedAppMaster);
        return from(this.persistentStorageService.set('[APP_MASTER]', updatedAppMaster)).pipe(
          take(1),
          map(() => {
            return action;
          }),
        );
      }),
      switchMap((action) => {
        return this.remoteDeploymentStoreFacade.getUpdateDetails().pipe(
          take(1),
          map((res) => {
            return res.updatesInfo?.updateUrl as string;
          }),
        );
      }),
      switchMap((updateUrl) => {
        return from(
          this.persistentStorageService.clearExceptKeys([
            '[APP_MASTER]',
            '[APP][INPUT_APP_DEVICES_DB]',
            '[APP][INPUT_APP_BACK_UP_DATA_STATUS]',
          ]),
        ).pipe(
          take(1),
          switchMap(() => {
            return from(this.inputAppErrorUtil.dismissLoadingWithId('UPDATING_APP_MASTER')).pipe(
              take(1),
              switchMap(() => {
                return from(AppUpdater.downloadAppAndInstall({ appDownloadUrl: updateUrl })).pipe(
                  take(1),
                  map(() => {
                    window.location.reload();
                    return new NoRemoteDeploymentAction();
                  }),
                );
              }),
            );
          }),
        );
      }),
    );
  });
}
